import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { User, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { staffPosition, USUAL_OPERATOR_ROLES } from '../utils/staffRoles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OperatorAssignmentModal = ({ open, onClose, operationId, operationIds, currentOperatorId, onSuccess }) => {
  const [operators, setOperators] = useState([]);
  const [selectedOperatorId, setSelectedOperatorId] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingOperators, setFetchingOperators] = useState(true);
  
  // Support both single operation and bulk operations
  const isBulkMode = operationIds && operationIds.length > 0;
  const targetOperations = isBulkMode ? operationIds : [operationId].filter(Boolean);

  const usualOperators = operators.filter(op => USUAL_OPERATOR_ROLES.includes((op.role || '').toLowerCase()));
  const otherStaff = operators.filter(op => !USUAL_OPERATOR_ROLES.includes((op.role || '').toLowerCase()));
  const selectedStaff = operators.find(op => op.id === selectedOperatorId);
  const isCrossRole = !!selectedStaff && !USUAL_OPERATOR_ROLES.includes((selectedStaff.role || '').toLowerCase());

  useEffect(() => {
    if (open) {
      fetchOperators();
      setSelectedOperatorId(currentOperatorId || '');
      setReason('');
    }
  }, [open, currentOperatorId]);

  const fetchOperators = async () => {
    try {
      setFetchingOperators(true);
      // Tout le staff interne est assignable (PM 03/11/2026)
      const response = await axios.get(`${API}/internal-users/operators/list?is_active=true&include_all_staff=true`, {
        headers: {
          'Authorization': 'Bearer cookie-auth'
        }
      });
      setOperators(response.data);
    } catch (error) {
      console.error('Error fetching operators:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger la liste des opérateurs",
        variant: "destructive"
      });
    } finally {
      setFetchingOperators(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedOperatorId) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner un opérateur",
        variant: "destructive"
      });
      return;
    }

    if (targetOperations.length === 0) {
      toast({
        title: "Erreur",
        description: "Aucune opération sélectionnée",
        variant: "destructive"
      });
      return;
    }

    if (isCrossRole && !reason.trim()) {
      toast({
        title: "Motif requis",
        description: "Indiquez pourquoi ce profil est assigné à l'opération",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      if (isBulkMode) {
        // Bulk assignment
        const response = await axios.post(`${API}/bulk-actions/assign-operator`, {
          operationIds: targetOperations,
          operatorId: selectedOperatorId,
          reason: reason || undefined
        }, {
          headers: {
            'Authorization': 'Bearer cookie-auth'
          }
        });

        toast({
          title: "✅ Opérateur assigné",
          description: `${response.data.success_count} opération(s) mise(s) à jour`
        });

        if (response.data.failed_count > 0) {
          console.warn('Failed operations:', response.data.failed_operations);
        }
      } else {
        // Single operation assignment
        await axios.put(`${API}/transport-requests/${operationId}/operator`, {
          operatorId: selectedOperatorId,
          reason: reason || undefined
        }, {
          headers: {
            'Authorization': 'Bearer cookie-auth'
          }
        });

        toast({
          title: "✅ Opérateur assigné",
          description: "L'opérateur a été assigné avec succès"
        });
      }

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Error assigning operator:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible d'assigner l'opérateur",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-[#1db584]" />
            Assigner un responsable
          </DialogTitle>
          <DialogDescription>
            {isBulkMode 
              ? `Sélectionnez le membre du staff en charge de ${targetOperations.length} opération(s)`
              : "Sélectionnez le membre du staff en charge de cette opération — tous les profils internes sont disponibles"
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {fetchingOperators ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1db584] mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">Chargement des opérateurs...</p>
            </div>
          ) : operators.length === 0 ? (
            <Alert className="bg-orange-50 border-orange-200">
              <AlertCircle className="h-4 w-4 text-orange-600" />
              <AlertDescription className="text-orange-800">
                Aucun membre du staff actif trouvé. Créez au moins un utilisateur interne actif.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="operator">Responsable de l&apos;opération *</Label>
                <Select value={selectedOperatorId} onValueChange={setSelectedOperatorId}>
                  <SelectTrigger data-testid="operator-select-trigger">
                    <SelectValue placeholder="Sélectionner un membre du staff..." />
                  </SelectTrigger>
                  <SelectContent>
                    {usualOperators.length > 0 && (
                      <SelectGroup>
                        <SelectLabel>Équipe opérationnelle</SelectLabel>
                        {usualOperators.map(op => (
                          <SelectItem key={op.id} value={op.id} data-testid={`operator-option-${op.id}`}>
                            {op.firstName} {op.lastName} — {staffPosition(op)}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                    {otherStaff.length > 0 && (
                      <SelectGroup>
                        <SelectLabel>Autres équipes</SelectLabel>
                        {otherStaff.map(op => (
                          <SelectItem key={op.id} value={op.id} data-testid={`operator-option-${op.id}`}>
                            {op.firstName} {op.lastName} — {staffPosition(op)}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {isCrossRole && (
                <Alert className="bg-amber-50 border-amber-200" data-testid="operator-cross-role-warning">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800 text-xs">
                    <strong>{selectedStaff?.firstName} {selectedStaff?.lastName}</strong> n&apos;est pas
                    rattaché à l&apos;équipe opérationnelle. L&apos;assignation reste possible : elle sera
                    tracée dans l&apos;historique et la personne recevra une notification. Indiquez le motif.
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="reason">
                  Motif {isCrossRole ? '*' : '(optionnel)'}
                </Label>
                <Textarea
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ex. : remplacement congés, surcroît d'activité…"
                  rows={3}
                  data-testid="operator-reason-input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={loading}
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleAssign}
                  disabled={loading || !selectedOperatorId}
                  className="bg-[#1db584] hover:bg-[#16a075]"
                  data-testid="operator-assign-submit-btn"
                >
                  {loading ? 'Assignation...' : 'Assigner'}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OperatorAssignmentModal;
